import React, { useState, useEffect } from 'react';
import Modal from 'global/components/modal';
import useStyles from './styles';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Icon, Typography, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { get, size, set, uniq } from 'global/lib/lodash';
import clsx from 'clsx';
import setUserRealm from 'redux/actions/userRealm';
import { userHasPermissions } from 'redux/actions/userLogin';
import Styles, { Loader, useScreen } from 'global/styles';
import Alert from '@mui/material/Alert';
import { Access } from 'global/components/droprealms';
import { withRouter } from 'react-router-dom';
import { useUser } from 'global/hooks/useUser';
import { getOnlyTiersFromScorecards } from '../index'
import { Text } from '../../../../global/components/text';
import Form from '../../../../global/components/form';
import useRetrieveData from 'global/hooks/useRetrieveData';

const TemplateButton = (props) => {
  const { title, secondButton, onClick, subtitle } = props;
  const classes = useStyles();

  return (
    <div className={classes.flexRow}>
      <Button className={classes.templateButton} startIcon={<Icon>add</Icon>} onClick={onClick}>
        <div style={{ textAlign: 'left' }}>
          <div>{title}</div>
          {!!subtitle && <div><Text type="label_small" italic>{`for ${subtitle}`}</Text></div>}
        </div>
      </Button>
      {secondButton && (
        <Button className={clsx(classes.templateButton, classes.templateButton2)} onClick={secondButton.onClick}>
          <Icon>{secondButton.icon}</Icon>
        </Button>
      )}
    </div>
  );
};

const firstBracketMatch = (...strings) => {
  for (const str of strings) {
    const match = str?.match(/\[(.*?)\]/);
    if (match) return match[1];
  }
  return undefined;
}

const exceptBracketMatch = (string) => {
  return string?.replace(/\[.*?\]/, '');
}

const RealmReset = (props) => {
  const classes = useStyles();
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const dispatch = useDispatch();

  const onReset = () => {
    dispatch(setUserRealm(null));
  };

  const hasRealm = get(realm, 'id');

  return !hasRealm ? null : (
    <div className={classes.realmReset}>
      Showing available templates for <Icon>room</Icon>
      {get(realm, 'name')} <Button onClick={onReset}>RESET</Button>
    </div>
  );
};

export default withRouter((props) => {
  const { templates, onDefaultSelect, startTemplateCreate } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [selected, setSelected] = useState();
  const { isMobileXS } = useScreen();
  const { me } = useUser();
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const classes = useStyles({ isMobileXS });
  const styles = Styles();
  let [selRealm, setSelRealm] = useState();
  const [selectLotLevelInfo, setSelectLotLevelInfo] = useState();

  const hasPerm = userHasPermissions('createReportTemplates');
  const tempCopy = firstBracketMatch(selected?.name);

  const onEdit = (temp) => {
    props.history.push(`assessment-template/${temp.id}`);
  };

  const users = useRetrieveData(
    { url: '/users' },
    {
      useEffect: true,
      waitFor: !!selRealm && !!selRealm.id,
      build: ['users'],
      params: { filter: { content_realm_id: get(selRealm, 'id') } },
      useCache: { reload: 1 },
    }
  );

  const startSubmit = (temp, _r) => {
    try {
      const r = _r[Object.keys(_r)[0]];
      selRealm = r;
      setSelRealm(r);
      
      if (r.tier === 'lot') {
        return setSelectLotLevelInfo({});
      }

      submitTemplate(temp);
    } catch (err) {

    }
  }

  const submitTemplate = async (temp = selected, values = {}) => {
    try {      
      setLoading(true);
      const scorecards = {};
      Object.keys(temp.scores).map((k) => {
        set(scorecards, `scorecard_${k}`, k);
      });
      const err =
        props.onSelectTemplate &&
        (await props.onSelectTemplate({
          ...temp,
          ...scorecards,
          generalVisibility: get(temp, 'visibility'),
          template_id: temp.id,
          realmId: selRealm.id,
          accessRealmTier: get(selRealm, 'tier', 'division'),
          reportName: `${selRealm?.name || exceptBracketMatch(temp?.name)}${tempCopy ? ` - ${tempCopy}` : ''}`,
          ...values,
        }));
        
      if (err) {
        setError(err.message);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (temp) => {
    setSelected(temp);
  };

  useEffect(() => {
    if (props.open) {
      setSelected();
      setError();
      setLoading(false);
      setSelectLotLevelInfo();
    }
  }, [props.open]);

  const all = get(templates, 'data', []);
  const global = all.filter((t) => get(t, 'status') === 'global_template');
  const mine = all.filter((t) => get(t, 'status') === 'template' && (get(t, 'authors', []).find((u) => u.id === me.id) || get(t, 'editors', []).find((u) => u.id === me.id)));

  const hasMine = size(mine) > 0;

  const onlyTiers = (() => {
    if (!selected) { return ['division', 'community', 'lot'] }

    return getOnlyTiersFromScorecards(get(selected, 'scorecards', []));
  })();

  return (
    <Modal title={selected ? `NEW ${(get(selected, 'name') || '').toUpperCase()}` : 'CREATE ASSESSMENT'} {...props}>
      {loading && (
        <div className={clsx(classes.container, styles.centerColumn)}>
          <Loader />
        </div>
      )}

      {!loading && selected && !selectLotLevelInfo && (
        <div style={{ width: '100%', height: '100%' }}>
          <Access
            onlyTiers={onlyTiers}
            singleSelect={true}
            canAdd
            onSelectedChange={(sel) => {
              startSubmit(selected, sel);
            }}
            openToSelected={true}
            currentRealm={realm || selected.realm}
            openToChildren
          />
        </div>
      )}

      {!loading && selectLotLevelInfo && <div>
        <Form
          onSubmit={vals => {
            submitTemplate(selected, vals);
          }}
          showAllScreens
          canSubmit={true}
          useCheckContinue
          submitBtnBoxStyle={{ position: 'relative' }}
          screens={[{
            inputs: [
              {
                id: 'reportName',
                type: 'TextField',
                defaultValue: () => `${selRealm?.name || exceptBracketMatch(selected?.name)}${tempCopy ? ` - ${tempCopy}` : ''}`,
                props: {
                  label: 'Title',
                  required: true
                }
              },
              {
                id: 'assignees',
                type: 'UserSelect',
                defaultValue: () => null,
                data: () => users.data,
                props: {
                  label: 'Assignee',
                  required: true,
                  multiple: false,
                }
              }
            ]
          }]}
        />
      </div>}

      {!loading && !selected && !selectLotLevelInfo && (
        <div className={classes.container}>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
          <TemplateButton title="Custom Assessment" onClick={onDefaultSelect} />

          <div className={classes.grouping}>
            <RealmReset />
            {!!size(global) && (
              <>
                <Typography className={classes.header}>ALL TEMPLATES</Typography>
                {global.map((temp,i) => (
                  <TemplateButton key={i} onClick={() => selectTemplate(temp)} title={get(temp, 'name')} subtitle={get(temp, 'realm.name')} secondButton={{ icon: 'visibility', onClick: () => onEdit(temp) }} />
                ))}
              </>
            )}
          </div>

          {(hasPerm || hasMine) && (
            <div className={classes.grouping}>
              <div className={clsx(classes.flexRow)}>
                <Typography className={clsx(classes.header, classes.rowWidth)}>MY TEMPLATES</Typography>
                {hasPerm && (
                  <Button onClick={startTemplateCreate} className={clsx(classes.templateButton, classes.templateButton2)}>
                    <Icon>add</Icon>
                  </Button>
                )}
              </div>
              {mine.map((temp, i) => (
                <TemplateButton key={i} onClick={() => selectTemplate(temp)} title={get(temp, 'name')} subtitle={get(temp, 'realm.name')} secondButton={{ icon: 'edit', onClick: () => onEdit(temp) }} />
              ))}
              {!hasMine && <div className={classes.noneYet}>{`You haven't created any assessment templates yet`}</div>}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
});
